import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MusicLoader from "../../../helper/MusicLoader";
import BioLinkIcon from "../../../assets/images/admin/bio-link.png";
import { showToast } from "../../../helper/ShowToast";
import { useBioLinkStatus } from "../../../queries/artistManger/smartLink.query";
import * as actions from "../../../redux/actions/growthEngine";
import BioLink from "../LinkAndFanHub/BuildBioLink/BioLink";

const timeAgo = (date) => {
    const now = new Date();
    const past = new Date(date);
    const seconds = Math.floor((now - past) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30.44);
    const years = Math.floor(days / 365.25);

    if (seconds < 5) return "just now";
    if (seconds < 60) return `${seconds} seconds ago`;
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
    return `${years} year${years > 1 ? "s" : ""} ago`;
};

export const BioLinkStep = () => {
    const dispatch = useDispatch();
    const { formData } = useSelector((state) => state.growthEngine);
    const [view, setView] = useState("list");
    const [editLinkId, setEditLinkId] = useState(null);

    const {
        data: bioLinkStatus,
        isLoading,
        refetch,
    } = useBioLinkStatus();

    const bioLink = bioLinkStatus?.exists ? bioLinkStatus.data : null;

    const toggleBioLinkSelection = (link) => {
        const isSelected =
            String(formData.selectedBioLinkId) === String(link._id);

        if (isSelected) {
            dispatch(
                actions.updateFormData({
                    selectedBioLinkId: "",
                    destinationUrl: "",
                    selectedTargetImage: "",
                    selectedTargetTitle: "",
                })
            );
            showToast("info", "Bio link unselected");
            return;
        }

        if (!link?.fullLink) {
            showToast("error", "Bio link URL is missing");
            return;
        }
        dispatch(
            actions.updateFormData({
                selectedBioLinkId: link._id,
                destinationUrl: link.fullLink,
                selectedTargetImage: link.image || "",
                selectedTargetTitle: link.title || "",
            })
        );
        showToast("success", "Bio link selected for your ad");
    };

    const handleCreateSuccess = (createdLink) => {
        const link = createdLink?._id || createdLink?.id ? createdLink : createdLink?.data;
        const linkId = link?._id || link?.id;
        if (linkId && link?.fullLink) {
            dispatch(
                actions.updateFormData({
                    selectedBioLinkId: linkId,
                    destinationUrl: link.fullLink,
                    selectedTargetImage: link.image || "",
                    selectedTargetTitle: link.title || "",
                })
            );
        }
        setView("list");
        setEditLinkId(null);
        refetch();
    };

    const handleEditSuccess = (updatedLink) => {
        const link = updatedLink?._id || updatedLink?.id ? updatedLink : updatedLink?.data;
        const linkId = link?._id || link?.id;
        if (
            linkId &&
            link?.fullLink &&
            String(formData.selectedBioLinkId) === String(linkId)
        ) {
            dispatch(
                actions.updateFormData({
                    destinationUrl: link.fullLink,
                    selectedTargetImage: link.image || "",
                    selectedTargetTitle: link.title || "",
                })
            );
        }
        setView("list");
        setEditLinkId(null);
        refetch();
    };

    if (view === "create") {
        return (
            <div className="font-inter mb-8 xl:mb-[61px]" data-tour="bio-link-step">
                <div className="heading text-white font-medium text-center mb-[28.5px]">
                    <h5 className="text-[22.5px] mb-1">Create Bio Link</h5>
                    <div className="description text-white text-opacity-80 text-[15px]">
                        Build your bio link here and continue with your campaign.
                    </div>
                </div>
                <div className="info-box bg-white rounded-xl overflow-hidden">
                    <BioLink
                        embedded
                        hideAnalytics
                        onBack={() => {
                            setView("list");
                            setEditLinkId(null);
                        }}
                        onSuccess={handleCreateSuccess}
                    />
                </div>
            </div>
        );
    }

    if (view === "edit" && editLinkId) {
        return (
            <div className="font-inter mb-8 xl:mb-[61px]" data-tour="bio-link-step">
                <div className="heading text-white font-medium text-center mb-[28.5px]">
                    <h5 className="text-[22.5px] mb-1">Edit Bio Link</h5>
                    <div className="description text-white text-opacity-80 text-[15px]">
                        Update your bio link and return to campaign setup.
                    </div>
                </div>
                <div className="info-box bg-white rounded-xl overflow-hidden">
                    <BioLink
                        embedded
                        hideAnalytics
                        linkId={editLinkId}
                        onBack={() => {
                            setView("list");
                            setEditLinkId(null);
                        }}
                        onSuccess={handleEditSuccess}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="font-inter mb-8 xl:mb-[61px]" data-tour="bio-link-step">
            <div className="heading text-white font-medium text-center mb-[28.5px]">
                <h5 className="text-[22.5px] mb-1">Choose your Bio Link</h5>
                <div className="description text-white text-opacity-80 text-[15px]">
                    Select your bio link for the ad, or create one if you don't have it yet.
                </div>
            </div>

            <div className="info-box bg-white rounded-xl p-4 lg:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 pb-4 border-b border-theme_clr2 border-opacity-30">
                    <div>
                        <div className="title-name text-[22.69px] font-medium text-theme_clr1 mb-1">
                            Your Bio Link
                        </div>
                        <div className="description font-medium text-theme_clr1 text-opacity-55 text-[15px]">
                            Use this link as your ad destination URL.
                        </div>
                    </div>
                    {!bioLink && !isLoading && (
                        <button
                            type="button"
                            className="btn-custom flex text-nowrap items-center justify-center gap-2 !text-base !rounded-full shrink-0"
                            onClick={() => {
                                setEditLinkId(null);
                                setView("create");
                            }}
                        >
                            <img src={BioLinkIcon} alt="Bio Link" className="w-5 h-5 object-contain" />
                            Create Bio Link
                        </button>
                    )}
                </div>

                <div className="border-[2px] border-theme_clr1 rounded-xl px-3 md:px-4 py-2 bg-white">
                    {isLoading ? (
                        <div className="flex justify-center items-center py-10">
                            <MusicLoader />
                        </div>
                    ) : !bioLink ? (
                        <div className="text-center py-10 px-4">
                            <div className="text-lg font-bold text-theme_clr3 mb-2">
                                You don't have a Bio Link yet
                            </div>
                            <p className="text-theme_clr1 text-opacity-70 text-sm mb-6 max-w-md mx-auto">
                                Create a bio link first, then we'll use it as the destination URL for your campaign.
                            </p>
                            <button
                                type="button"
                                className="btn-custom inline-flex text-nowrap items-center justify-center gap-2 !text-base !rounded-full"
                                onClick={() => {
                                    setEditLinkId(null);
                                    setView("create");
                                }}
                            >
                                <img src={BioLinkIcon} alt="Bio Link" className="w-5 h-5 object-contain" />
                                Create Bio Link
                            </button>
                        </div>
                    ) : (
                        <div className="info-card-wrapper font-jakarta">
                            {(() => {
                                const isSelected =
                                    String(formData.selectedBioLinkId) === String(bioLink._id);

                                return (
                                    <div
                                        className={`info-card py-4 rounded-lg transition-colors ${
                                            isSelected
                                                ? "bg-theme_clr2/10 ring-2 ring-theme_clr2 ring-inset px-2 -mx-1"
                                                : ""
                                        }`}
                                    >
                                        <div className="flex flex-col lg:flex-row flex-wrap lg:items-center justify-between gap-4">
                                            <div className="flex items-start gap-3 min-w-0">
                                                <img
                                                    src={bioLink?.image || BioLinkIcon}
                                                    alt={bioLink?.title || "Bio Link"}
                                                    className="w-14 h-[72px] rounded-lg object-cover flex-shrink-0"
                                                />
                                                <div className="text-theme_clr1 min-w-0">
                                                    <div className="font-bold text-base capitalize mb-1 flex items-center gap-2 flex-wrap">
                                                        <span>{bioLink?.title || "Bio Link"}</span>
                                                        {isSelected && (
                                                            <span className="inline-flex items-center gap-1 bg-theme_clr2 text-white text-xs font-bold px-2 py-1 rounded">
                                                                <i className="bi bi-check-circle-fill"></i>
                                                                Selected
                                                            </span>
                                                        )}
                                                    </div>

                                                    {(bioLink?.createdAt || bioLink?.updatedAt) && (
                                                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-bold mb-[6px]">
                                                            {bioLink?.createdAt && (
                                                                <span>Created {timeAgo(bioLink.createdAt)} –</span>
                                                            )}
                                                            {bioLink?.updatedAt && (
                                                                <span>(Updated {timeAgo(bioLink.updatedAt)})</span>
                                                            )}
                                                        </div>
                                                    )}

                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <a
                                                            href={bioLink?.fullLink}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="bg-theme_clr2 bg-opacity-55 font-bold text-sm rounded flex items-center gap-6 px-3 py-1 max-w-full"
                                                        >
                                                            <span className="truncate">{bioLink?.fullLink}</span>
                                                            <span>
                                                                <i className="bi bi-link-45deg"></i>
                                                            </span>
                                                        </a>
                                                        <span>
                                                            <i className="bi bi-box-arrow-up-right"></i>
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="font-bold flex items-center flex-wrap gap-x-4 lg:gap-x-6 gap-y-2 text-theme_clr1 text-base">
                                                <button
                                                    type="button"
                                                    className={`px-4 py-2 rounded-full border-2 font-semibold transition ${
                                                        isSelected
                                                            ? "bg-theme_clr1 border-theme_clr1 text-white hover:bg-transparent hover:text-theme_clr1"
                                                            : "border-theme_clr1 text-theme_clr1 hover:bg-theme_clr1 hover:text-white"
                                                    }`}
                                                    onClick={() => toggleBioLinkSelection(bioLink)}
                                                >
                                                    {isSelected ? "Unselect" : "Select"}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="hover:text-theme_clr3 transition"
                                                    onClick={() => {
                                                        setEditLinkId(bioLink._id);
                                                        setView("edit");
                                                    }}
                                                >
                                                    Edit Site
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
